import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Loader2, Check, ImageIcon } from "lucide-react";
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';

// Icon families for colorful flat icons (provider-specific)
const ICON_FAMILIES = [
  { id: 3, name: 'Basic Straight Flat' },
  { id: 8, name: 'Special Flat' },
];

interface Icon {
  id: number;
  name: string;
  slug: string;
  thumbnails: Array<{
    width: number;
    height: number;
    url: string;
  }>;
  family?: {
    id: number;
    name: string;
  };
}

interface FreepikIconSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectIcon: (iconUrl: string) => void;
  initialQuery?: string;
  categoryGroupSlug?: string;
}

const FreepikIconSearchModal: React.FC<FreepikIconSearchModalProps> = ({
  isOpen,
  onClose,
  onSelectIcon,
  initialQuery = '',
  categoryGroupSlug = '',
}) => {
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [icons, setIcons] = useState<Icon[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedIconId, setSelectedIconId] = useState<number | null>(null);
  const [selectedFamily, setSelectedFamily] = useState(ICON_FAMILIES[1].id); // Default to Special Flat

  const searchIcons = useCallback(async (query: string, familyId: number) => {
    if (!query.trim()) return;

    setIsLoading(true);
    try {
      // Get auth token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Not authenticated');
      }

      // Call Edge Function for icon search
      const { data, error } = await supabase.functions.invoke('search-icons', {
        body: {
          query,
          familyId,
          perPage: 20,
        },
      });

      if (error) {
        throw error;
      }

      if (!data.success) {
        throw new Error(data.error || 'Search failed');
      }

      setIcons(data.icons || []);

      if (!data.icons || data.icons.length === 0) {
        toast({
          title: "No icons found",
          description: "Try a different search term or icon style",
          variant: "default",
        });
      }
    } catch (error) {
      console.error('Error searching icons:', error);
      toast({
        title: "Search failed",
        description: "Could not fetch icons from Freepik. Please try again.",
        variant: "destructive",
      });
      setIcons([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Auto-search when modal opens with initial query
  React.useEffect(() => {
    if (isOpen && initialQuery.trim()) {
      setSearchQuery(initialQuery);
      searchIcons(initialQuery, selectedFamily);
    }
  }, [isOpen, initialQuery, selectedFamily, searchIcons]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSelectedIconId(null);
    searchIcons(searchQuery, selectedFamily);
  };

  const handleFamilyChange = (familyId: number) => {
    setSelectedFamily(familyId);
    if (searchQuery.trim()) {
      setSelectedIconId(null);
      searchIcons(searchQuery, familyId);
    }
  };

  const handleSelectIcon = (icon: Icon) => {
    setSelectedIconId(icon.id);
  };

  const handleConfirmSelection = async () => {
    const selectedIcon = icons.find(icon => icon.id === selectedIconId);
    if (!selectedIcon) return;

    setIsUploading(true);
    try {
      // Call Edge Function to download and upload icon
      const { data, error } = await supabase.functions.invoke('upload-icon', {
        body: {
          iconId: selectedIcon.id,
          categoryGroupSlug: categoryGroupSlug || 'category',
          iconName: selectedIcon.slug || selectedIcon.name,
        },
      });

      if (error) {
        throw error;
      }

      if (!data.success) {
        throw new Error(data.error || 'Upload failed');
      }

      if (!data.url) {
        throw new Error('No icon URL returned');
      }

      toast({
        title: "Icon uploaded",
        description: `Category group icon has been set successfully (${data.format?.toUpperCase()})`,
      });

      // Return the public URL
      onSelectIcon(data.url);
      handleClose();

    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Upload failed",
        description: "An unexpected error occurred while uploading the icon",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setIcons([]);
    setSelectedIconId(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[900px] max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Search Category Group Icons</DialogTitle>
          <DialogDescription>
            Search for colorful, professional icons from Freepik to represent your category group.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col">
          {/* Search Form */}
          <form onSubmit={handleSearch} className="space-y-3 mb-4">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  type="text"
                  placeholder="Search for icons (e.g., 'restaurant', 'education', 'health')"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button type="submit" disabled={isLoading || !searchQuery.trim()}>
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
              </Button>
            </div>

            {/* Icon Style Selector */}
            <div className="flex gap-2">
              {ICON_FAMILIES.map((family) => (
                <Button
                  key={family.id}
                  type="button"
                  variant={selectedFamily === family.id ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleFamilyChange(family.id)}
                  disabled={isLoading}
                >
                  {family.name}
                </Button>
              ))}
            </div>
          </form>

          {/* Icon Grid */}
          <div className="flex-1 overflow-y-auto">
            {icons.length > 0 ? (
              <div className="grid grid-cols-4 md:grid-cols-6 gap-3">
                {icons.map((icon) => (
                  <div
                    key={icon.id}
                    className={`relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all p-3 hover:shadow-md ${
                      selectedIconId === icon.id
                        ? 'border-primary ring-2 ring-primary/20 bg-primary/5'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => handleSelectIcon(icon)}
                  >
                    <div className="aspect-square flex items-center justify-center">
                      <img
                        src={icon.thumbnails[0]?.url}
                        alt={icon.name}
                        className="w-full h-full object-contain"
                        loading="lazy"
                      />
                    </div>

                    {/* Selection indicator */}
                    {selectedIconId === icon.id && (
                      <div className="absolute top-1 right-1 bg-primary text-white rounded-full p-1">
                        <Check className="h-3 w-3" />
                      </div>
                    )}

                    {/* Icon name */}
                    <div className="mt-2">
                      <p className="text-xs text-center text-gray-600 truncate">
                        {icon.name}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                <ImageIcon className="h-12 w-12 mb-4 opacity-50" />
                <p className="text-lg font-medium">
                  {isLoading ? "Searching..." : "Search for category icons"}
                </p>
                <p className="text-sm text-center max-w-md mt-2">
                  {isLoading
                    ? "Loading professional icons from Freepik..."
                    : "Enter keywords related to your category group (e.g., 'food', 'education', 'health')"}
                </p>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="secondary" onClick={handleClose} disabled={isUploading}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirmSelection}
            disabled={!selectedIconId || isUploading}
          >
            {isUploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Uploading...
              </>
            ) : (
              'Use Selected Icon'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FreepikIconSearchModal;
